/**
 * ConnectionStatus - Shows realtime connection status indicator
 * Displays when WebSocket is disconnected or has errors
 * Also shows Internet connectivity status
 * Only shows when user is authenticated
 */
import React, { useState, useEffect } from 'react';
import { useWebSocket } from '../../context/WebSocketContext';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Wifi, WifiOff, AlertCircle } from 'lucide-react';

const ConnectionStatus = () => {
  const { connectionStatus, manualReconnect } = useWebSocket();
  const { isAuthenticated } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOnlineMessage, setShowOnlineMessage] = useState(false);
  
  // Monitor internet connectivity
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOnlineMessage(true);
      // Hide the "back online" message after 3 seconds
      setTimeout(() => setShowOnlineMessage(false), 3000);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      setShowOnlineMessage(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Don't show anything if not authenticated (e.g., on login page)
  if (!isAuthenticated) {
    return null;
  }
  
  // Show "back online" message
  if (isOnline && showOnlineMessage) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg border bg-emerald-500/10 border-emerald-500/20 text-emerald-400 text-sm shadow-lg backdrop-blur-sm animate-in slide-in-from-bottom-2"
        data-testid="connection-online"
      >
        <Wifi className="w-4 h-4" />
        <span>Interneti u rikthye!</span>
      </div>
    );
  }
  
  // Show offline message (highest priority)
  if (!isOnline) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg border bg-orange-500/10 border-orange-500/20 text-orange-400 text-sm shadow-lg backdrop-blur-sm"
        data-testid="connection-offline"
      >
        <WifiOff className="w-4 h-4" />
        <span>Nuk keni internet</span>
      </div>
    );
  }
  
  // Only show WS status when not connected and online
  if (connectionStatus === 'connected') {
    return null;
  }
  
  const statusConfig = {
    connecting: {
      icon: <RefreshCw className="w-4 h-4 animate-spin" />,
      text: 'Duke u lidhur...',
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/20',
      textColor: 'text-yellow-400',
    },
    disconnected: {
      icon: <WifiOff className="w-4 h-4" />,
      text: 'Realtime i ndërprerë',
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/20',
      textColor: 'text-orange-400',
    },
    error: {
      icon: <AlertCircle className="w-4 h-4" />,
      text: 'Gabim lidhje',
      bg: 'bg-red-500/10',
      border: 'border-red-500/20',
      textColor: 'text-red-400',
    },
  };
  
  const config = statusConfig[connectionStatus] || statusConfig.disconnected;
  
  return (
    <div 
      className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-lg border ${config.bg} ${config.border} ${config.textColor} text-sm shadow-lg backdrop-blur-sm`}
      data-testid="connection-status"
    >
      {config.icon}
      <span>{config.text}</span>
      {connectionStatus !== 'connecting' && (
        <button
          onClick={manualReconnect}
          className="ml-2 p-1 hover:bg-white/10 rounded transition-colors"
          title="Rilidhu"
        >
          <RefreshCw className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export default ConnectionStatus;
